import { observer } from 'mobx-react';
import { useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Save, Send, ArrowLeft, Plus, Search, X, Loader2 } from 'lucide-react';
import {
  OOC_REASONS,
  OOCForecastAdjustment,
  OOCForecastCell,
  OOCProvFacilityRow,
  formatOOCNumber,
  formatOOCDate,
} from '../oocForecast.type';
import OOCPackSizeChips from './OOCPackSizeChips';
import {
  useOOCCreateEditLogic,
  TAB_CONFIG,
  WORKFLOW_STEPS,
  PROBABILITY_OPTIONS,
  getFcColorClass,
  getStatusBadgeClass,
  getStatusLabel,
  periodToLabel,
  ndcResultKey,
} from './oocCreateEdit.logic';
import classes from './oocCreateEdit.module.scss';
import { useStore } from 'hooks/useStore';
import { provDataKey } from 'stores/fdpStore/oocStore';

const REASON_OPTIONS = OOC_REASONS.map((r) => ({ label: r, value: r }));

const WorkflowProgressBar = ({ currentStep }: { currentStep: number }) => (
  <div className={classes.workflowBar}>
    {WORKFLOW_STEPS.map((ws, idx) => {
      const isCompleted = ws.step < currentStep;
      const isActive = ws.step === currentStep;
      const circleClass = isCompleted
        ? classes.workflowCircleCompleted
        : isActive
        ? classes.workflowCircleActive
        : classes.workflowCircleInactive;
      const labelClass = isCompleted
        ? classes.workflowStepLabelCompleted
        : isActive
        ? classes.workflowStepLabelActive
        : '';
      return (
        <div key={ws.step} style={{ display: 'flex', alignItems: 'flex-start' }}>
          {idx > 0 && (
            <div className={`${classes.workflowConnector} ${isCompleted ? classes.workflowConnectorCompleted : ''}`} />
          )}
          <div className={classes.workflowStep}>
            <div className={`${classes.workflowCircle} ${circleClass}`}>
              {isCompleted ? '✓' : ws.step}
            </div>
            <span className={`${classes.workflowStepLabel} ${labelClass}`}>{ws.label}</span>
          </div>
        </div>
      );
    })}
  </div>
);

const FCColorLegend = () => (
  <div className={classes.fcLegendBar}>
    <span className={classes.fcLegendLabel}>FC Color Legend:</span>
    {[
      { color: '#d4edda', label: 'New' },
      { color: '#ffe0b2', label: 'Modified' },
      { color: '#ffcdd2', label: 'Removed' },
      { color: '#ffffff', label: 'Unchanged', bordered: true },
    ].map(({ color, label, bordered }) => (
      <span key={label} className={classes.fcLegendItem}>
        <span
          className={classes.fcLegendSwatch}
          style={{ backgroundColor: color, ...(bordered ? { border: '1px solid #ccc' } : {}) }}
        />
        {label}
      </span>
    ))}
  </div>
);

const StatusBadge = ({ status }: { status: string }) => (
  <span className={`${classes.statusBadge} ${getStatusBadgeClass(status, classes)}`}>
    {getStatusLabel(status)}
  </span>
);

const ForecastCell = observer(
  ({
    cell,
    adjustmentId,
    periodIndex,
    totalPeriods,
    onCopyForward,
  }: {
    cell: OOCForecastCell;
    adjustmentId: string;
    periodIndex: number;
    totalPeriods: number;
    onCopyForward: (id: string, fromIdx: number) => void;
  }) => {
    const { OOCStore } = useStore();
    const [isFocused, setIsFocused] = useState(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value.replace(/,/g, '').trim();
      const parsed = raw === '' ? null : Number(raw);
      if (raw !== '' && isNaN(parsed as number)) return;
      OOCStore.updateForecastValue(adjustmentId, periodIndex, parsed);
    };

    const displayValue =
      cell.value !== null && cell.value !== undefined ? formatOOCNumber(cell.value) : '';
    const showCopyDot = isFocused && periodIndex < totalPeriods - 1 && cell.value !== null;

    return (
      <td className={classes.colFc}>
        <div className={classes.fcCellWrap}>
          {cell.previousValue !== null && cell.colorType === 'modified' && (
            <span className={classes.fcPreviousValue}>was: {formatOOCNumber(cell.previousValue)}</span>
          )}
          <input
            className={`${classes.fcInput} ${getFcColorClass(cell.colorType, classes)}`}
            type="text"
            value={displayValue}
            onChange={handleChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setTimeout(() => setIsFocused(false), 150)}
            placeholder="-"
          />
          {showCopyDot && (
            <span
              className={classes.copyForwardDot}
              title="Copy to all following months"
              onMouseDown={(e) => {
                e.preventDefault();
                onCopyForward(adjustmentId, periodIndex);
              }}
            >
              .
            </span>
          )}
        </div>
      </td>
    );
  }
);

const AdjustmentRow = observer(
  ({ adjustment, showProbability }: { adjustment: OOCForecastAdjustment; showProbability: boolean }) => {
    const { OOCStore } = useStore();
    const [isRemoving, setIsRemoving] = useState(false);
    const isLoadingBaseline = OOCStore.loadingBaselineNDCs.includes(adjustment.ndc11);
    const isMissingReason = OOCStore.missingReasonIds.includes(adjustment.id);

    const handleRemove = async () => {
      setIsRemoving(true);
      await OOCStore.removeAdjustment(adjustment.id);
      setIsRemoving(false); // only reached when removal failed — success unmounts this row
    };

    const handleCopyForward = (adjustmentId: string, fromIndex: number) => {
      const sourceValue = adjustment.forecasts[fromIndex]?.value;
      if (sourceValue === null || sourceValue === undefined) return;
      for (let i = fromIndex + 1; i < adjustment.forecasts.length; i++) {
        OOCStore.updateForecastValue(adjustmentId, i, sourceValue);
      }
    };

    return (
      <tr>
        <td className={classes.colNdc10}>{adjustment.ndc10}</td>
        <td className={classes.colNdc11}>{adjustment.ndc11}</td>
        <td className={classes.colSupplier}>{adjustment.supplier}</td>
        <td className={classes.colMolecule}>{adjustment.molecule}</td>
        <td className={classes.colConcept}>{adjustment.concept}</td>
        <td className={classes.colPackOut}>{adjustment.packOut}</td>
        <td className={classes.colReason}>
          <Dropdown
            className={`${classes.reasonDropdown} ${isMissingReason ? 'p-invalid' : ''}`}
            options={REASON_OPTIONS}
            value={adjustment.reason ?? null}
            onChange={(e) => OOCStore.updateAdjustmentReason(adjustment.id, e.value)}
            placeholder="Select reason"
            filter
            filterPlaceholder="Search reason..."
          />
          {isMissingReason && <span className={classes.reasonErrorMsg}>Required</span>}
        </td>
        {isLoadingBaseline
          ? adjustment.forecasts.map((cell) => (
              <td key={cell.period} className={classes.colFc}>
                <div className={classes.skeletonCell} />
              </td>
            ))
          : adjustment.forecasts.map((cell, pIdx) => (
              <ForecastCell
                key={cell.period}
                cell={cell}
                adjustmentId={adjustment.id}
                periodIndex={pIdx}
                totalPeriods={adjustment.forecasts.length}
                onCopyForward={handleCopyForward}
              />
            ))}
        <td className={classes.colNotes}>
          <input
            className={classes.notesInput}
            type="text"
            value={adjustment.notes}
            onChange={(e) => OOCStore.updateNotes(adjustment.id, e.target.value)}
            placeholder="Add notes..."
          />
        </td>
        {showProbability && (
          <td className={classes.colProbability}>
            <Dropdown
              className={classes.probabilityDropdown}
              options={PROBABILITY_OPTIONS}
              value={adjustment.probability ?? null}
              onChange={(e) => OOCStore.updateProbability(adjustment.id, e.value)}
              placeholder="Select"
            />
          </td>
        )}
        <td className={classes.colRemove}>
          <button className={classes.removeBtn} onClick={handleRemove} disabled={isRemoving} title="Remove NDC">
            {isRemoving ? <Loader2 size={14} className={classes.removeBtnSpinner} /> : <X size={16} />}
          </button>
        </td>
      </tr>
    );
  }
);

const ReferenceDataPanel = ({ adjustments }: { adjustments: OOCForecastAdjustment[] }) => {
  const withRef = adjustments.filter((a) => a.referenceData);
  if (withRef.length === 0) return null;

  return (
    <div className={classes.referenceDataSection}>
      <h4 className={classes.referenceDataSectionTitle}>FC Reference Data</h4>
      <div className={classes.referenceDataGrid}>
        {withRef.map((adj) => {
          const ref = adj.referenceData!;
          const isNegative = ref.percentChange.startsWith('-');
          const isPositive = ref.percentChange.startsWith('+');
          const changeClass = isNegative ? classes.negativeChange : isPositive ? classes.positiveChange : '';
          return (
            <div key={adj.id} className={classes.referenceDataBox}>
              <div className={classes.referenceDataTitle}>FC Reference Data ({ref.ndc11})</div>
              {[
                { label: '12-mo SO Avg', value: formatOOCNumber(ref.twelveMonthSOAvg) },
                { label: '6-mo SO Avg', value: formatOOCNumber(ref.sixMonthSOAvg) },
                { label: 'Current Month', value: formatOOCNumber(ref.currentMonthPerformance) },
                { label: 'Market Demand', value: formatOOCNumber(ref.overallMarketDemand) },
                { label: 'New FC', value: formatOOCNumber(ref.newFC) },
              ].map(({ label, value }) => (
                <div key={label} className={classes.referenceRow}>
                  <span className={classes.referenceLabel}>{label}</span>
                  <span className={classes.referenceValue}>{value}</span>
                </div>
              ))}
              <div className={classes.referenceRow}>
                <span className={classes.referenceLabel}>% Change</span>
                <span className={`${classes.referenceValue} ${changeClass}`}>{ref.percentChange}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ProvNdcHeader = observer(
  ({ adjustment, isMissingReason }: { adjustment: OOCForecastAdjustment; isMissingReason: boolean }) => {
    const { OOCStore } = useStore();
    const [isRemoving, setIsRemoving] = useState(false);

    const handleRemove = async () => {
      setIsRemoving(true);
      await OOCStore.removeAdjustment(adjustment.id);
      setIsRemoving(false); // only reached when removal failed — success unmounts this row
    };

    return (
      <div className={classes.provNdcBlockHeader}>
        <span className={classes.provNdcBadge}>{adjustment.ndc11}</span>
        <span className={classes.provNdcMeta}>{adjustment.concept}</span>
        <div className={classes.provNdcHeaderControls}>
          <Dropdown
            className={`${classes.reasonDropdown} ${isMissingReason ? 'p-invalid' : ''}`}
            options={REASON_OPTIONS}
            value={adjustment.reason ?? null}
            onChange={(e) => OOCStore.updateAdjustmentReason(adjustment.id, e.value)}
            placeholder="Select reason"
            filter
            filterPlaceholder="Search reason..."
          />
          <input
            className={classes.notesInput}
            type="text"
            value={adjustment.notes}
            onChange={(e) => OOCStore.updateNotes(adjustment.id, e.target.value)}
            placeholder="Add notes..."
          />
          {isMissingReason && (
            <span className={classes.provNdcMissingReasonTag} style={{ marginLeft: 0 }}>
              Reason required
            </span>
          )}
          <button className={classes.removeBtn} onClick={handleRemove} disabled={isRemoving} title="Remove NDC">
            {isRemoving ? <Loader2 size={14} className={classes.removeBtnSpinner} /> : <X size={16} />}
          </button>
        </div>
      </div>
    );
  }
);

const OOCCreateEdit = () => {
  const {
    OOCStore,
    periods,
    moleculeGroups,
    showProbability,
    showSearchResults,
    selectedNDCs,
    handleSearchChange,
    handleSearchKeyDown,
    handleToggleNDCCheckbox,
    handleToggleMoleculeCheckbox,
    isMoleculeFullyChecked,
    isMoleculeIndeterminate,
    isAddingNDC,
    handleAddNDCButtonClick,
    handleSearchBlur,
    handleSearchFocus,
    showLeaveWarning,
    handleCancelClick,
    handleConfirmLeave,
    handleDismissLeaveWarning,
    handleSaveAndLeave,
  } = useOOCCreateEditLogic();

  const { currentSubmission, activeTab, activeTabAdjustments, tabCounts, ndcSearchQuery, ndcSearchResults, isNDCSearching, isSaving } = OOCStore;

  if (!currentSubmission) {
    return (
      <div className={classes.container}>
        <div className={classes.emptyState}>No submission loaded.</div>
      </div>
    );
  }

  // ─── Provisional MAS: CMOP/COT facility grid ─────────────────
  const renderProvMasGrid = (adj: OOCForecastAdjustment) => {
    const provKey = provDataKey(adj.ndc11, 'ProvMAS');
    const isLoading = OOCStore.loadingProvForecastNDCs.includes(provKey);
    const data = OOCStore.provForecastData[provKey];

    if (isLoading) {
      return (
        <div className={classes.provGridLoading}>
          <Loader2 size={18} className={classes.provGridSpinner} />
          <span>Loading facility parameters…</span>
        </div>
      );
    }

    if (!data || data.facilityRows.length === 0) {
      return (
        <div className={classes.provGridEmpty}>
          Provisional forecast parameters not yet available for this NDC.
          Data will be populated from <strong>FCT_ProvisionalForecast</strong> for the current planning period.
        </div>
      );
    }

    const rows = data.facilityRows;
    const selectedCMOPCount = rows.filter((r) => r.cot === 'CMOP' && r.isSelected).length;
    const packNum = parseInt(adj.packOut, 10) || 1;

    // Matches ProvisionalForecastV2/Edit/MasForecastGrid.tsx exactly: EU is
    // annualVolSU (already in EU terms, no pack-size conversion) times the
    // month's percentage — no /12, no pack-size division. Subtotals/totals
    // sum the raw (unrounded) monthly EU values and round once at display,
    // same as the canonical grid, so figures match to the digit.
    // annualVolSU itself is kept in sync with the pack-size checkboxes by
    // OOCStore.toggleProvPackSelected (see recomputeProvVolumes), so EU
    // updates live as packs are checked/unchecked.
    const monthlyEU = (row: OOCProvFacilityRow, mIdx: number): number => {
      if (!row.isSelected) return 0;
      const pct = row.monthlyPercentages[mIdx] ?? 0;
      return row.annualVolSU * (pct / 100);
    };

    // SU (Selling Units) — matches ProvisionalForecastV2/Edit/SUPricesTable.tsx
    // exactly: Σ(annual_market / packsize) across CHECKED packs for the
    // facility, summed once, then scaled by the month's %. This is NOT the
    // same as "EU ÷ each known packsize" (the old formula) — that double-
    // counts whenever a facility carries more than one pack size, and never
    // reacted to the checkbox since it read the static packSizes list
    // instead of the live selection.
    const calcPacks = new Set(
      (data.selectedPackSizes ?? [])
        .filter((p) => p.selected && p.used_in_calc)
        .map((p) => p.packsize)
    );
    const annualSUByFacility = new Map<string, number>();
    for (const mktRow of data.marketData ?? []) {
      if (!calcPacks.has(mktRow.packsize)) continue;
      const packSize = parseFloat(mktRow.packsize);
      if (!Number.isFinite(packSize) || packSize <= 0) continue;
      const annualMarketEU = parseFloat(mktRow.annual_market);
      if (!Number.isFinite(annualMarketEU)) continue;
      annualSUByFacility.set(
        mktRow.facility_cot,
        (annualSUByFacility.get(mktRow.facility_cot) ?? 0) + annualMarketEU / packSize
      );
    }
    const monthlySURaw = (row: OOCProvFacilityRow, mIdx: number): number => {
      if (!row.isSelected) return 0;
      const pct = row.monthlyPercentages[mIdx] ?? 0;
      const annualSU = annualSUByFacility.get(row.facilityName);
      if (annualSU !== undefined) return annualSU * (pct / 100);
      // Fallback for legacy/empty marketData (e.g. GFC has no
      // AnnualMarketSales pack-size data on file) — same convention as
      // the "Communicate to SC" email/Excel export.
      const eu = monthlyEU(row, mIdx);
      return packNum ? eu / packNum : 0;
    };

    // Row grouping matches the canonical Provisional Forecast MAS grid
    // (ProvisionalForecastV2/Edit/MasForecastGrid.tsx): everything except
    // MTF/TMOP is grouped under "CMOP" for subtotal purposes (this
    // includes "VA Facility"/VISN rows). CMOP Subtotal sits directly after
    // the CMOP-group rows and before MTF/TMOP, and is hidden if either
    // group is empty (it would just duplicate the grand TOTAL row).
    const NON_CMOP_FACILITY_COTS = new Set(['MTF', 'TMOP']);
    const indexedRows = rows.map((row, idx) => ({ row, idx }));
    const cmopGroupRows = indexedRows.filter(({ row }) => !NON_CMOP_FACILITY_COTS.has(row.cot));
    const nonCmopGroupRows = indexedRows.filter(({ row }) => NON_CMOP_FACILITY_COTS.has(row.cot));
    const showCmopSubtotal = cmopGroupRows.length > 0 && nonCmopGroupRows.length > 0;

    // Sum the PER-ROW ROUNDED values (the same whole numbers displayed in
    // each facility row), not the raw unrounded values — otherwise the
    // subtotal/total can be off by 1-2 from what a user gets by manually
    // adding up the numbers they can actually see on screen.
    const cmopTotals = periods.map((_, mi) =>
      cmopGroupRows.reduce((s, { row }) => s + Math.round(monthlyEU(row, mi)), 0)
    );
    const grandTotals = periods.map((_, mi) =>
      rows.reduce((s, r) => s + Math.round(monthlyEU(r, mi)), 0)
    );
    const cmopSUTotals = periods.map((_, mi) =>
      cmopGroupRows.reduce((s, { row }) => s + Math.round(monthlySURaw(row, mi)), 0)
    );
    const grandSUTotals = periods.map((_, mi) =>
      rows.reduce((s, r) => s + Math.round(monthlySURaw(r, mi)), 0)
    );

    const S = { fac: 162, vol: 108 };
    const stickyStyle = (left: number, bg = 'inherit'): React.CSSProperties => ({
      position: 'sticky',
      left,
      zIndex: 2,
      background: bg,
    });
    const stickyThStyle = (left: number): React.CSSProperties => ({
      position: 'sticky',
      left,
      top: 0,
      zIndex: 3,
      background: 'var(--bg-th-market-strategy)',
    });
    const lastStickyBorder: React.CSSProperties = {
      borderRight: '2px solid var(--border-primary)',
      boxShadow: '3px 0 6px -2px rgba(0,0,0,0.15)',
    };

    const renderFacilityRow = (row: OOCProvFacilityRow, rIdx: number) => {
      const rowBg = row.isContingency
        ? '#fffbeb'
        : rIdx % 2 === 0
        ? 'var(--bg-card)'
        : 'var(--bg-secondary)';
      return (
        <tr key={rIdx}>
          <td style={{ ...stickyStyle(0, rowBg), width: S.fac, minWidth: S.fac }}>
            <label className={classes.provFacLabel}>
              <Checkbox
                checked={row.isSelected}
                onChange={() => OOCStore.toggleProvFacility(adj.ndc11, rIdx)}
              />
              <span style={{ opacity: row.isSelected ? 1 : 0.45 }}>{row.facilityName}</span>
              {row.isContingency && <span className={classes.provContBadge}>CONT</span>}
            </label>
          </td>
          <td
            style={{
              ...stickyStyle(S.fac, rowBg),
              width: S.vol,
              minWidth: S.vol,
              ...lastStickyBorder,
              opacity: row.isSelected ? 1 : 0.45,
            }}
          >
            <span className={classes.provVolCell}>{row.annualVolSU.toLocaleString()}</span>
          </td>
          {periods.map((_, mi) => {
            const pct = row.monthlyPercentages[mi] ?? 0;
            const eu = Math.round(monthlyEU(row, mi));
            const su = Math.round(monthlySURaw(row, mi));
            return (
              <td key={mi} className={classes.provFacCell} style={{ opacity: row.isSelected ? 1 : 0.3 }}>
                {row.isSelected && (
                  <>
                    {/* Every month is editable, matching MasForecastGrid.tsx.
                        The anchor-month → cascade-forward "copy" rule lives in
                        OOCStore.updateProvFacilityPct; editing any other month
                        here just updates that single cell. */}
                    <input
                      type="number"
                      className={classes.provPctInput}
                      value={pct}
                      min={0}
                      max={100}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) =>
                        OOCStore.updateProvFacilityPct(adj.ndc11, rIdx, mi, Number(e.target.value))
                      }
                    />
                    <span className={classes.provEuVal}>EU: {eu.toLocaleString()}</span>
                    <span className={classes.provSuVal}>SU: {su.toLocaleString()}</span>
                  </>
                )}
              </td>
            );
          })}
        </tr>
      );
    };

    return (
      <div className={classes.provCmopWrapper}>
        <OOCPackSizeChips
          ndc11={adj.ndc11}
          marketData={data.marketData}
          selectedPackSizes={data.selectedPackSizes}
          onToggle={(ndc11, packsize) => OOCStore.toggleProvPackSelected(ndc11, 'ProvMAS', packsize)}
        />
        {selectedCMOPCount > 0 && selectedCMOPCount <= 2 && (
          <div className={classes.provExceptionBanner}>
            ⚠ Only {selectedCMOPCount} CMOP{selectedCMOPCount > 1 ? 's' : ''} selected — per business rules, planning
            100% of demand for fewer than 3 purchasing CMOPs.
          </div>
        )}
        <div style={{ overflowX: 'auto' }}>
          <table className={classes.provFacTable}>
            <thead>
              <tr>
                <th style={{ ...stickyThStyle(0), width: S.fac, minWidth: S.fac }}>Facility</th>
                <th style={{ ...stickyThStyle(S.fac), width: S.vol, minWidth: S.vol, ...lastStickyBorder }}>
                  Annual Vol (SU)
                </th>
                {periods.map((p) => (
                  <th key={p} className={classes.provMonthTh}>
                    {periodToLabel(p)}
                    <div className={classes.provMonthSub}>% / EU / SU</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cmopGroupRows.map(({ row, idx: rIdx }) => renderFacilityRow(row, rIdx))}
              {showCmopSubtotal && (
                <tr className={classes.provSubtotalRow}>
                  <td style={{ ...stickyStyle(0, '#f0ebff'), width: S.fac, minWidth: S.fac }}>
                    <strong>CMOP Subtotal</strong>
                  </td>
                  <td style={{ ...stickyStyle(S.fac, '#f0ebff'), width: S.vol, minWidth: S.vol, ...lastStickyBorder }} />
                  {cmopTotals.map((v, mi) => (
                    <td key={mi} className={classes.provFacCell}>
                      <strong className={classes.provEuVal}>EU: {v.toLocaleString()}</strong>
                      <strong className={classes.provSuVal}>SU: {cmopSUTotals[mi].toLocaleString()}</strong>
                    </td>
                  ))}
                </tr>
              )}
              {nonCmopGroupRows.map(({ row, idx: rIdx }) => renderFacilityRow(row, rIdx))}
              <tr className={classes.provTotalRow}>
                <td style={{ ...stickyStyle(0, '#ede9fe'), width: S.fac, minWidth: S.fac }}>
                  <strong>TOTAL</strong>
                </td>
                <td style={{ ...stickyStyle(S.fac, '#ede9fe'), width: S.vol, minWidth: S.vol, ...lastStickyBorder }} />
                {grandTotals.map((v, mi) => (
                  <td key={mi} className={classes.provFacCell}>
                    <strong className={classes.provEuVal}>EU: {v.toLocaleString()}</strong>
                    <strong className={classes.provSuVal}>SU: {grandSUTotals[mi].toLocaleString()}</strong>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ─── Provisional NC: variance + 3-month forecast panel ───────
  const renderProvNcPanel = (adj: OOCForecastAdjustment) => {
    const provKey = provDataKey(adj.ndc11, 'ProvNC');
    const isLoading = OOCStore.loadingProvForecastNDCs.includes(provKey);
    const data = OOCStore.provForecastData[provKey];

    if (isLoading) {
      return (
        <div className={classes.provGridLoading}>
          <Loader2 size={18} className={classes.provGridSpinner} />
          <span>Loading NCE parameters…</span>
        </div>
      );
    }

    if (!data || data.varianceRows.length === 0) {
      return (
        <div className={classes.provGridEmpty}>
          Provisional NCE parameters not yet available for this NDC.
          Data will be populated from <strong>FCT_ProvisionalForecast</strong> for the current planning period.
        </div>
      );
    }

    const provNcMonthPcts = OOCStore.provNcMonthPcts;
    const provNcThreshold = OOCStore.provNcThreshold;
    const ncMonthLabels = periods.slice(0, 3).map(periodToLabel);
    const rows = data.varianceRows;

    const totalHist = rows.reduce((s, r) => s + r.historical12MVolume, 0);
    const totalNCE = rows.reduce((s, r) => s + r.nceVolume, 0);
    const totalVarPct = totalHist > 0 ? ((totalNCE - totalHist) / totalHist) * 100 : 0;
    const isTotalFlagged = Math.abs(totalVarPct) > provNcThreshold;

    const getMonthFC = (nceVolume: number, mIdx: number): number => {
      const monthly = Math.round(nceVolume / 12);
      return monthly + Math.round(monthly * (provNcMonthPcts[mIdx] / 100));
    };

    const monthTotals = [0, 1, 2].map((mIdx) =>
      rows.reduce((s, r) => s + getMonthFC(r.nceVolume, mIdx), 0)
    );

    const AGENCY_COLORS: Record<string, string> = { VA: '#4d2e6d', DOD: '#0e7490' };

    return (
      <div className={classes.provNcPanel}>
        <OOCPackSizeChips
          ndc11={adj.ndc11}
          marketData={data.marketData}
          selectedPackSizes={data.selectedPackSizes}
          onToggle={(ndc11, packsize) => OOCStore.toggleProvPackSelected(ndc11, 'ProvNC', packsize)}
        />
        <div className={classes.provNcControls}>
          <table className={classes.provNcPctTable}>
            <thead>
              <tr>
                <th>Month</th>
                <th>% Applied to Hist 12M ÷ 12</th>
              </tr>
            </thead>
            <tbody>
              {([0, 1, 2] as const).map((mIdx) => (
                <tr key={mIdx}>
                  <td className={classes.provNcMonthCell}>{ncMonthLabels[mIdx]}</td>
                  <td>
                    <div className={classes.provNcPctRow}>
                      <InputText
                        className={classes.provNcPctInput}
                        value={String(provNcMonthPcts[mIdx])}
                        onChange={(e) => {
                          const val = Math.max(0, Math.min(200, parseInt(e.target.value) || 0));
                          const next = [...provNcMonthPcts] as [number, number, number];
                          next[mIdx] = val;
                          OOCStore.updateProvNcSettings(next, provNcThreshold);
                        }}
                      />
                      <span>%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className={classes.provNcThresholdRow}>
            <label>Flag threshold:</label>
            <InputNumber
              value={provNcThreshold}
              onValueChange={(e) => OOCStore.updateProvNcSettings(provNcMonthPcts, e.value ?? 15)}
              suffix="%"
              min={1}
              max={100}
              inputStyle={{ width: '70px', textAlign: 'right' }}
            />
          </div>
          <p className={classes.provNcFormula}>
            Forecast = Annual NCE ÷ 12 + Annual NCE ÷ 12 × %. Defaults: 50% / 75% / 90%.
          </p>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className={classes.provNcTable}>
            <thead>
              <tr>
                <th colSpan={4} style={{ background: 'var(--bg-th-market-strategy)' }} />
                <th colSpan={3} className={classes.provNcFcGroupHeader}>
                  3-Month Provisional Forecast
                </th>
              </tr>
              <tr>
                <th className={classes.provNcTh}>Agency</th>
                <th className={`${classes.provNcTh} ${classes.provNcThBlue}`}>Historical 12M (SUs)</th>
                <th className={`${classes.provNcTh} ${classes.provNcThBlue}`}>Annual NCE (SUs)</th>
                <th className={`${classes.provNcTh} ${classes.provNcThBlue}`}>Variance</th>
                {ncMonthLabels.map((lbl, mIdx) => (
                  <th key={mIdx} className={`${classes.provNcTh} ${classes.provNcThGreen}`}>
                    {lbl}
                    <div className={classes.provNcThSub}>@ {provNcMonthPcts[mIdx]}%</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const varPct =
                  row.historical12MVolume > 0
                    ? ((row.nceVolume - row.historical12MVolume) / row.historical12MVolume) * 100
                    : 0;
                const isFlagged = Math.abs(varPct) > provNcThreshold;
                return (
                  <tr key={row.agency}>
                    <td
                      className={classes.provNcTd}
                      style={{ fontWeight: 700, color: AGENCY_COLORS[row.agency] ?? '#000' }}
                    >
                      {row.agency}
                    </td>
                    <td className={classes.provNcTd} style={{ textAlign: 'right' }}>
                      {row.historical12MVolume.toLocaleString()}
                    </td>
                    <td className={classes.provNcTd}>
                      <InputNumber
                        value={row.nceVolume}
                        onValueChange={(e) => OOCStore.updateProvNCE(adj.ndc11, row.agency, e.value ?? row.nceVolume)}
                        mode="decimal"
                        useGrouping
                        min={0}
                        style={{ width: '100%' }}
                        inputStyle={{ textAlign: 'right', width: '100%' }}
                      />
                    </td>
                    <td
                      className={classes.provNcTd}
                      style={{
                        textAlign: 'right',
                        fontWeight: 700,
                        color: isFlagged ? '#dc2626' : varPct > 0 ? '#15803d' : '#1d4ed8',
                        background: isFlagged ? '#fef2f2' : undefined,
                      }}
                    >
                      {varPct > 0 ? '+' : ''}
                      {varPct.toFixed(1)}%{isFlagged && ' ⚠'}
                    </td>
                    {([0, 1, 2] as const).map((mIdx) => (
                      <td
                        key={mIdx}
                        className={classes.provNcTd}
                        style={{ textAlign: 'right', background: '#f9fefb', fontWeight: 700, color: '#4d2e6d' }}
                      >
                        {getMonthFC(row.nceVolume, mIdx).toLocaleString()}
                      </td>
                    ))}
                  </tr>
                );
              })}
              <tr className={classes.provNcTotalRow}>
                <td className={classes.provNcTd}>
                  <strong>TOTAL</strong>
                </td>
                <td className={classes.provNcTd} style={{ textAlign: 'right' }}>
                  <strong>{totalHist.toLocaleString()}</strong>
                </td>
                <td className={classes.provNcTd} style={{ textAlign: 'right' }}>
                  <strong>{totalNCE.toLocaleString()}</strong>
                </td>
                <td
                  className={classes.provNcTd}
                  style={{
                    textAlign: 'right',
                    fontWeight: 700,
                    color: isTotalFlagged ? '#dc2626' : totalVarPct > 0 ? '#15803d' : '#1d4ed8',
                  }}
                >
                  {totalVarPct > 0 ? '+' : ''}
                  {totalVarPct.toFixed(1)}%{isTotalFlagged && ' ⚠'}
                </td>
                {monthTotals.map((tot, mIdx) => (
                  <td key={mIdx} className={classes.provNcTd} style={{ textAlign: 'right', background: '#f0fdf4' }}>
                    <strong>{tot.toLocaleString()}</strong>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
        <p className={classes.provNcNote}>
          Cells marked ⚠ exceed the {provNcThreshold}% variance threshold.
        </p>
      </div>
    );
  };

  return (
    <div className={classes.container}>
      <WorkflowProgressBar currentStep={1} />

      <div className={classes.sectionHeader}>
        <h2 className={classes.sectionTitle}>New OOC Submission - {currentSubmission.id}</h2>
        <StatusBadge status={currentSubmission.status} />
      </div>

      <div className={classes.metaFieldsRow}>
        {[
          { label: 'Submission Date', value: formatOOCDate(currentSubmission.submissionDate) },
          { label: 'Submitted By', value: currentSubmission.submittedBy },
          { label: 'Planning Period', value: currentSubmission.planningPeriod },
        ].map(({ label, value }) => (
          <div key={label} className={classes.formGroup}>
            <label className={classes.formLabel}>{label}</label>
            <InputText className={`${classes.formInput} ${classes.readonlyInput}`} value={value} readOnly />
          </div>
        ))}
      </div>

      {/* <div className={`${classes.callout} ${classes.calloutInfo}`}>
        <span className={classes.calloutIcon}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        </span>
        <span>
          <strong>Ramping File Integration:</strong> NDCs from the approved ramping file are automatically pre-populated below. You can adjust forecast values, add new NDCs, assign a reason per NDC, and remove entries as needed before communicating to Supply Chain.
        </span>
      </div> */}

      <div className={classes.ndcSearchRow}>
        <div className={classes.ndcSearchInputWrap}>
          <Search size={16} className={classes.ndcSearchIcon} />
          <InputText
            className={classes.ndcSearchInput}
            value={ndcSearchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search by NDC, molecule, or concept to add..."
            onFocus={handleSearchFocus}
            onBlur={handleSearchBlur}
          />
          {!isNDCSearching && showSearchResults && ndcSearchResults.length > 0 && (
            <div className={classes.ndcSearchResults}>
              {Object.entries(moleculeGroups).map(([molecule, ndcs]) => (
                <div key={`group-${molecule}`}>
                  {ndcs.length > 1 && (
                    <div
                      className={`${classes.ndcSearchResultItem} ${classes.ndcGroupHeader}`}
                      onMouseDown={(e) => { e.preventDefault(); handleToggleMoleculeCheckbox(molecule); }}
                    >
                      <div className={classes.ndcResultCheckboxRow}>
                        <input
                          type="checkbox"
                          className={classes.ndcCheckbox}
                          checked={isMoleculeFullyChecked(molecule)}
                          ref={(el) => { if (el) el.indeterminate = isMoleculeIndeterminate(molecule); }}
                          onChange={() => {}}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className={classes.ndcResultInfo}>
                          <span className={classes.ndcResultNdc}>{molecule}</span>
                          <span className={classes.ndcResultMeta}>{ndcs.map((n) => n.ndc11).join(', ')}</span>
                        </div>
                      </div>
                      <span className={`${classes.ndcResultTab} ${classes.ndcGroupTag}`}>{ndcs.length} NDCs</span>
                    </div>
                  )}
                  {ndcs.map((ndc) => (
                    <div
                      key={ndcResultKey(ndc)}
                      className={`${classes.ndcSearchResultItem} ${ndcs.length > 1 ? classes.ndcChildItem : ''}`}
                      onMouseDown={(e) => { e.preventDefault(); handleToggleNDCCheckbox(ndcResultKey(ndc)); }}
                    >
                      <div className={classes.ndcResultCheckboxRow}>
                        <input
                          type="checkbox"
                          className={classes.ndcCheckbox}
                          checked={selectedNDCs.has(ndcResultKey(ndc))}
                          onChange={() => {}}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className={classes.ndcResultInfo}>
                          <span className={classes.ndcResultNdc}>{ndc.ndc11}</span>
                          <span className={classes.ndcResultMeta}>{ndc.supplier} | {ndc.molecule} | {ndc.concept}</span>
                        </div>
                      </div>
                      <span className={classes.ndcResultTab}>
                        → {TAB_CONFIG.find((t) => t.key === ndc.suggestedTab)?.label ?? ndc.suggestedTab}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
          {isNDCSearching && (
            <div className={classes.ndcSearchResults}>
              <div className={classes.ndcSearchResultItem}>
                <span className={classes.ndcResultMeta}>Searching...</span>
              </div>
            </div>
          )}
        </div>
        <Button
          className={classes.addNdcBtn}
          icon={<Plus size={16} style={{ marginRight: 4 }} />}
          label={selectedNDCs.size > 0 ? `Add ${selectedNDCs.size} NDC${selectedNDCs.size > 1 ? 's' : ''}` : 'Add NDC'}
          onClick={handleAddNDCButtonClick}
          loading={isAddingNDC}
        />
      </div>

      <div className={classes.tabsRow}>
        {TAB_CONFIG.map((tab) => {
          const isActive = activeTab === tab.key;
          const hasError = OOCStore.tabsWithMissingReasons.has(tab.key);
          return (
            <button
              key={tab.key}
              className={`${classes.tabButton} ${isActive ? classes.tabButtonActive : ''} ${hasError ? classes.tabButtonError : ''}`}
              onClick={() => OOCStore.setActiveTab(tab.key)}
            >
              {tab.label}
              <span className={`${classes.tabCount} ${isActive ? classes.tabCountActive : ''}`}>
                {tabCounts[tab.key]}
              </span>
              {hasError && <span className={classes.tabErrorDot} title="Some NDCs are missing a reason" />}
            </button>
          );
        })}
      </div>

      <FCColorLegend />

      {activeTab !== 'ProvMAS' && activeTab !== 'ProvNC' && (
        <div className={classes.tableWrapper}>
          {activeTabAdjustments.length === 0 ? (
            <div className={classes.emptyState}>No NDCs in this tab. Use the search above to add NDCs.</div>
          ) : (
            <table className={classes.forecastTable}>
              <thead>
                <tr>
                  <th className={classes.colNdc10}>NDC10</th>
                  <th className={classes.colNdc11}>NDC11</th>
                  <th className={classes.colSupplier}>Supplier</th>
                  <th className={classes.colMolecule}>Molecule</th>
                  <th className={classes.colConcept}>Concept</th>
                  <th className={classes.colPackOut}>Pack Out</th>
                  <th className={classes.colReason}>Reason</th>
                  {periods.map((p) => (
                    <th key={p} className={classes.colFc}>{periodToLabel(p)}</th>
                  ))}
                  <th className={classes.colNotes}>Notes</th>
                  {showProbability && <th className={classes.colProbability}>Probability</th>}
                  <th className={classes.colRemove}></th>
                </tr>
              </thead>
              <tbody>
                {activeTabAdjustments.map((adj) => (
                  <AdjustmentRow key={adj.id} adjustment={adj} showProbability={showProbability} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab !== 'ProvMAS' && activeTab !== 'ProvNC' && (
        <ReferenceDataPanel adjustments={activeTabAdjustments} />
      )}

      {(activeTab === 'ProvMAS' || activeTab === 'ProvNC') && activeTabAdjustments.length === 0 && (
        <div className={classes.emptyState}>No NDCs in this tab. Use the search above to add NDCs.</div>
      )}

      {(activeTab === 'ProvMAS' || activeTab === 'ProvNC') && activeTabAdjustments.length > 0 && (
        <div className={classes.provCard}>
          <div className={classes.provCardHeader}>
            <div className={classes.provCardTitle}>Provisional Forecast Parameters</div>
            <span
              className={`${classes.provCardBadge} ${
                activeTab === 'ProvMAS' ? classes.provCardBadgeMas : classes.provCardBadgeNc
              }`}
            >
              {activeTab === 'ProvMAS' ? 'Prov. MAS' : 'Prov. NC'}
            </span>
          </div>
          <p className={classes.provCardDesc}>
            {activeTab === 'ProvMAS'
              ? 'Configure facility percentages for each NDC. M1–M4 are editable; M5–M15 follow propagation rules. Select CMOP facilities to plan demand allocation.'
              : 'Enter Annual NCE volumes for each agency. The 3-month provisional forecast is calculated from historical 12-month demand.'}
          </p>
          {activeTabAdjustments.map((adj) => {
            const isProvMissingReason = OOCStore.missingReasonIds.includes(adj.id);
            return (
              <div
                key={adj.id}
                className={`${classes.provNdcBlock} ${isProvMissingReason ? classes.provNdcBlockError : ''}`}
              >
                <ProvNdcHeader adjustment={adj} isMissingReason={isProvMissingReason} />
                {activeTab === 'ProvMAS' ? renderProvMasGrid(adj) : renderProvNcPanel(adj)}
              </div>
            );
          })}
        </div>
      )}

      <div className={classes.actionBar}>
        <div className={classes.actionBarLeft}>
          <Button
            className={`p-button-text ${classes.cancelBtn}`}
            icon={<ArrowLeft size={16} style={{ marginRight: 4 }} />}
            label="Cancel"
            onClick={handleCancelClick}
          />
        </div>
        <div className={classes.actionBarRight}>
          <Button
            className={`p-button-outlined ${classes.saveDraftBtn}`}
            icon={<Save size={16} style={{ marginRight: 4 }} />}
            label="Save Draft"
            onClick={() => OOCStore.saveDraft()}
            loading={isSaving}
          />
          <Button
            className={classes.communicateBtn}
            icon={<Send size={16} style={{ marginRight: 4 }} />}
            label="Communicate to SC"
            onClick={() => OOCStore.communicateToSC()}
            loading={isSaving}
            disabled={currentSubmission.adjustments.length === 0}
          />
        </div>
      </div>

      <Dialog
        visible={showLeaveWarning}
        onHide={handleDismissLeaveWarning}
        header="Exit form?"
        modal
        closable
        footer={
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <Button
              label="Cancel"
              outlined
              onClick={handleDismissLeaveWarning}
            />
            <Button
              label="Leave without saving"
              severity="danger"
              disabled={isSaving}
              onClick={handleConfirmLeave}
            />
            <Button
              label="Save Draft & leave"
              disabled={isSaving}
              loading={isSaving}
              onClick={handleSaveAndLeave}
              style={{ backgroundColor: '#522d6e', borderColor: '#522d6e' }}
            />
          </div>
        }
      >
        <p>You might have unsaved changes. Do you really want to exit the form?</p>
      </Dialog>
    </div>
  );
};

export default observer(OOCCreateEdit);
